import React from 'react';

import { useFlags } from '@atlaskit/flag';
import SuccessIcon from '@atlaskit/icon/core/check-mark';
import ErrorIcon from '@atlaskit/icon/core/status-error';
import { auth } from '@atlaskit/outbound-auth-flow-client';
import { fg } from '@atlaskit/platform-feature-flags';
import { G300, R400 } from '@atlaskit/theme/colors';
import { token } from '@atlaskit/tokens';
import {
	generate3pHash,
	SEARCH_3P_CONFIG,
	searchPageQueryCache,
	sendSlackAuthSuccessRequest,
	sendTeamsAuthSuccessRequest,
} from '@atlassian/search-client';
import { getLocalStorageItemAsString, setLocalStorageItem } from '@atlassian/search-common';

import { type NounKeys } from '../../common/constants/nouns';
import {
	is3pProductKey,
	type ProductKeys,
	type ProductKeys3P,
} from '../../common/constants/products';
import {
	type ConnectorSource,
	isForgeConnectorProductKey,
	type ThirdPartyConfigsBootstrap,
} from '../../common/constants/schemas/3p-config';
import { useAppContext } from '../../common/ui/app-context';
import type { OAuthSource } from '../../common/utils/events/oauth-events';
import { useFireOAuthCompleted, useFireOAuthFailed, useFireOAuthStarted } from '../events/use-fire-oauth-events';
import { useBootstrap, useSetThirdPartyConfigs } from '../store/bootstrap';
import { useQuickFindActions } from '../store/quick-find';
import { useSearchActions } from '../store/search';

interface OAuthHandlersProps {
	product: ProductKeys | NounKeys | undefined;
	connectorSource?: ConnectorSource;
	successFlagTitle: React.ReactNode;
	failFlagTitle: React.ReactNode;
	outboundAuthUrl: string;
	onCompletedAction?: () => void;
	onDismissAction?: () => void;
	onFailAction?: () => void;
	isOnSearchDialog?: boolean;
	isBulkConnect?: boolean;
	query?: string;
	datasourceId?: string;
}

const refreshOAuthCache = (
	product: ProductKeys,
	userId: string,
	cloudId: string,
	thirdPartyConfigs: ThirdPartyConfigsBootstrap,
	datasourceId?: string,
): ThirdPartyConfigsBootstrap => {
	const key = generate3pHash(userId, cloudId);
	const newThirdPartyConfigs = { ...thirdPartyConfigs };

	const item = getLocalStorageItemAsString(SEARCH_3P_CONFIG);

	if (!item) {
		return newThirdPartyConfigs;
	}
	let object;
	try {
		object = JSON.parse(item);
	} catch {
		return newThirdPartyConfigs;
	}

	// If datasource is provided, find and update specific datasource config
	if (datasourceId) {
		const thirdPartyConfigCache = object?.[key]?.result?.find(
			(config: any) => config.datasourceId === datasourceId,
		);

		if (!thirdPartyConfigCache) {
			return newThirdPartyConfigs;
		}

		// Update the OAuth status for this specific datasource
		thirdPartyConfigCache.isUserOAuthed = true;

		// Update the bootstrap config for the specific datasource
		const updatedConfig = newThirdPartyConfigs[product as ProductKeys3P];
		if (updatedConfig) {
			const datasourceIndex = updatedConfig.datasources?.findIndex(
				(ds) => ds.datasourceId === datasourceId,
			);
			if (datasourceIndex !== undefined && datasourceIndex >= 0 && updatedConfig.datasources) {
				updatedConfig.datasources[datasourceIndex].userNeedsOAuth = false;
			}
		}
	} else {
		// If no datasource provided, find and update base product config
		const thirdPartyConfigCache = object?.[key]?.result?.find(
			(config: any) =>
				config.id === product ||
				/// can be removed after statis config is rolled out
				config.type.includes(product) ||
				(config.integrationId.includes('google') && product === 'drive'),
		);

		if (!thirdPartyConfigCache) {
			return newThirdPartyConfigs;
		}

		// Update the OAuth status for the base product
		thirdPartyConfigCache.isUserOAuthed = true;

		// Update the bootstrap config for the base product
		const updatedConfig = newThirdPartyConfigs[product as ProductKeys3P];
		if (updatedConfig) {
			updatedConfig.userNeedsOAuth = false;
		}
	}

	setLocalStorageItem(SEARCH_3P_CONFIG, JSON.stringify(object));
	return newThirdPartyConfigs;
};

export const useOAuthHandlers: ({ product, connectorSource, successFlagTitle, failFlagTitle, outboundAuthUrl, onCompletedAction, onDismissAction, onFailAction, isOnSearchDialog, isBulkConnect, query, datasourceId, }: OAuthHandlersProps) => {
    onAuthorize: (oAuthSource: OAuthSource) => void;
    onDismiss: () => void;
    onSuccess: (oAuthSource: OAuthSource) => void;
    onFail: (oAuthSource: OAuthSource) => void;
} = ({
	product,
	connectorSource,
	successFlagTitle,
	failFlagTitle,
	outboundAuthUrl,
	onCompletedAction = () => {},
	onDismissAction = () => {},
	onFailAction = () => {},
	isOnSearchDialog,
	isBulkConnect,
	query = '',
	datasourceId,
}: OAuthHandlersProps) => {
	const [{ cloudId, user, thirdPartyConfigs }] = useBootstrap();
	const setThirdPartyConfigs = useSetThirdPartyConfigs();
	const { generateSearchUrl, queryParams } = useAppContext();
	const userId = user?.id;
	const { retrySearch } = useSearchActions();
	const { showFlag } = useFlags();
	const { setQuickFindOpen } = useQuickFindActions();
	const oAuthAnalyticsProps = {
		product: product || '',
		connectorSource,
		cloudId: cloudId || '',
		isOnSearchDialog,
		thirdPartyConfigs,
	};

	const fireOAuthStartedEvent = useFireOAuthStarted(oAuthAnalyticsProps);
	const fireOAuthCompletedEvent = useFireOAuthCompleted(oAuthAnalyticsProps);
	const fireOAuthFailedEvent = useFireOAuthFailed(oAuthAnalyticsProps);

	const onSuccess = (oAuthSource: OAuthSource): void => {
		fireOAuthCompletedEvent(oAuthSource);
		if (product === 'slack') {
			sendSlackAuthSuccessRequest({ cloudId });
		}
		if (product === 'teams' && fg('ai_3p_is_teams_platform_connector_enabled')) {
			sendTeamsAuthSuccessRequest({ cloudId });
		}

		onCompletedAction();

		showFlag({
			title: successFlagTitle,
			icon: <SuccessIcon spacing="spacious" label="" color={token('color.icon.success', G300)} />,
			isAutoDismiss: true,
		});

		const newOutboundAuthConfigs = refreshOAuthCache(
			product as ProductKeys3P,
			userId || '',
			cloudId || '',
			thirdPartyConfigs || {},
			datasourceId,
		);

		setThirdPartyConfigs(newOutboundAuthConfigs);
		searchPageQueryCache.clear();
		retrySearch();

		if (!isBulkConnect && isOnSearchDialog) {
			setQuickFindOpen(false);
			/* TODO: use onNavigate callback once bootstrap store is lifted. Details in https://hello.atlassian.net/wiki/spaces/CASE/pages/4670854655/3P+Discoverability+Bug
			const onNavigateCallback =
				typeof onNavigate === 'function' ? onNavigate : onNavigate.callback;
			onNavigateCallback(
				generateSearchUrl({
					...queryParams,
					text: query,
					product: product,
				}),
				'push',
			);
			*/
			const searchUrl = generateSearchUrl({
				...queryParams,
				text: query,
				product: product,
			});
			window.location.href = searchUrl;
		}
	};

	const onFail = (oAuthSource: OAuthSource): void => {
		fireOAuthFailedEvent(oAuthSource);
		onFailAction();
		showFlag({
			title: failFlagTitle,
			icon: <ErrorIcon spacing="spacious" label="" color={token('color.icon.danger', R400)} />,
			isAutoDismiss: true,
		});
	};

	const onAuthorize = (oAuthSource: OAuthSource): void => {
		fireOAuthStartedEvent(oAuthSource);
		auth(outboundAuthUrl)
			.then(() => onSuccess(oAuthSource))
			.catch(() => onFail(oAuthSource));
	};

	const onDismiss = (): void => {
		onDismissAction();
	};

	return { onAuthorize, onDismiss, onSuccess, onFail };
};

export const useIsUserOAuthed: (productKey: ProductKeys | NounKeys | undefined, selectedDatasourceId?: string | undefined) => {
    userNeedsOAuth: undefined;
    outboundAuthUrl: undefined;
} | {
    userNeedsOAuth: boolean;
    outboundAuthUrl: string;
} = (
	productKey: ProductKeys | NounKeys | undefined,
	selectedDatasourceId?: string | undefined,
) => {
	const [{ thirdPartyConfigs }] = useBootstrap();
	return isUserOAuthed(productKey, thirdPartyConfigs, selectedDatasourceId);
};

export const isUserOAuthed: (productKey: ProductKeys | NounKeys | undefined, thirdPartyConfigs: ThirdPartyConfigsBootstrap, selectedDatasourceId?: string) => {
    userNeedsOAuth: undefined;
    outboundAuthUrl: undefined;
} | {
    userNeedsOAuth: boolean;
    outboundAuthUrl: string;
} = (
	productKey: ProductKeys | NounKeys | undefined,
	thirdPartyConfigs: ThirdPartyConfigsBootstrap,
	selectedDatasourceId?: string,
) => {
	let userNeedsOAuth, outboundAuthUrl;

	// Handle Forge connectors first (they're not in is3pProductKey)
	if (productKey && isForgeConnectorProductKey(productKey)) {
		const productOutboundAuthConfig = thirdPartyConfigs[productKey];
		if (!productOutboundAuthConfig) {
			return { userNeedsOAuth, outboundAuthUrl };
		}
		userNeedsOAuth = productOutboundAuthConfig.userNeedsOAuth;
		outboundAuthUrl = productOutboundAuthConfig.outboundAuthUrl;
		return { userNeedsOAuth, outboundAuthUrl };
	}

	if (!productKey || !is3pProductKey(productKey)) {
		return { userNeedsOAuth, outboundAuthUrl };
	}

	// If datasource is provided, check specific datasource config
	if (selectedDatasourceId) {
		const datasourceOutboundAuthConfig = thirdPartyConfigs[productKey]?.datasources?.find(
			(datasource) => datasource.datasourceId === selectedDatasourceId,
		);
		if (!datasourceOutboundAuthConfig) {
			return { userNeedsOAuth, outboundAuthUrl };
		}
		userNeedsOAuth = datasourceOutboundAuthConfig.userNeedsOAuth;
		outboundAuthUrl = datasourceOutboundAuthConfig.outboundAuthUrl;

		return { userNeedsOAuth, outboundAuthUrl };
	}

	// If no datasource provided, check base product config
	const productOutboundAuthConfig = thirdPartyConfigs[productKey];
	if (!productOutboundAuthConfig) {
		return { userNeedsOAuth, outboundAuthUrl };
	}
	userNeedsOAuth = productOutboundAuthConfig.userNeedsOAuth;
	outboundAuthUrl = productOutboundAuthConfig.outboundAuthUrl;

	return { userNeedsOAuth, outboundAuthUrl };
};
